import { Button } from '@/components';
import { SendIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export function ChatInput({
    onSubmit,
    isSending,
    className,
}: {
    onSubmit: (message: string) => void;
    isSending: boolean;
    className?: string;
}) {
    const [message, setMessage] = useState('');
    const [isExpanded, setIsExpanded] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Function to check for overflow and adjust rows
    const checkOverflow = () => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        // Reset to 1 row to get accurate scrollHeight measurement
        textarea.style.height = 'auto';
        const scrollHeight = textarea.scrollHeight;
        const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10);

        // If content overflows one line, expand to 4 rows
        if (scrollHeight > lineHeight) {
            setIsExpanded(true);
        } else {
            setIsExpanded(false);
        }
    };

    // Check overflow whenever message changes
    useEffect(() => {
        checkOverflow();
    }, [message]);

    const handleSubmit = () => {
        onSubmit(message);
        setMessage('');
        setIsExpanded(false);
    };

    return (
        <form
            className={
                'flex items-center justify-center rounded-3xl border border-neutral-700 bg-neutral-800 p-2 pl-5 transition-all duration-200 ease-in-out' +
                (isExpanded ? ' h-30' : ' h-13') +
                className
            }
            onSubmit={e => {
                e.preventDefault();
                handleSubmit();
            }}
        >
            <textarea
                ref={textareaRef}
                rows={isExpanded ? 4 : 1}
                placeholder='Ask me anything...'
                className='no-scrollbar w-full resize-none border-none bg-transparent text-sm text-white outline-none focus:ring-0 focus:outline-none'
                value={message}
                onChange={e => setMessage(e.target.value)}
                onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit();
                    }
                }}
            />
            <Button
                size='icon'
                className='ml-2 self-end rounded-full bg-neutral-600/50'
                type='submit'
                loading={isSending}
            >
                <SendIcon />
            </Button>
        </form>
    );
}
